import React, { useState, useEffect } from 'react';
import { X, Loader2, Send } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; return null; }
  return res.json();
};

/**
 * Store Selector Modal
 *
 * Props:
 *   productIds     - array of product entity_ids to generate drafts for
 *   onClose        - () => void
 *   onRequestPush  - (storeId: number, productIds: number[]) => void,
 *                    当用户选择了店铺并点击确认后触发，由父组件接管后续进度
 */
const StoreSelectorModal = ({ productIds = [], onClose, onRequestPush }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      try {
        const res = await apiFetch('/api/channel/stores');
        if (res?.status === 'success') {
          setStores(res.data?.stores || []);
        } else {
          notify('Failed to load stores', 'error');
        }
      } catch (e) {
        notify('Error loading stores', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, []);

  const handleSubmit = () => {
    if (!selectedStoreId) {
      notify(t('common.please_select') || 'Please select a store', 'warning');
      return;
    }
    setSubmitting(true);
    // 通知父组件，由父组件接管进度和后续 API 调用
    onRequestPush?.(parseInt(selectedStoreId), productIds);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            {t('channel.push_to_channel') || '推送到渠道'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('channel.push_hint', { count: productIds.length }) ||
              `即将为 ${productIds.length} 个产品生成渠道草稿，请选择目标店铺：`}
          </p>

          {/* Store Select */}
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={20} className="animate-spin text-slate-400" />
            </div>
          ) : stores.length === 0 ? (
            <p className="text-sm text-amber-600 text-center py-4">
              {t('channel.no_stores') || '暂无可用店铺，请先在渠道管理中配置店铺'}
            </p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {stores.map(store => (
                <label
                  key={store.auth_id}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedStoreId === String(store.auth_id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="store"
                    value={store.auth_id}
                    checked={selectedStoreId === String(store.auth_id)}
                    onChange={e => setSelectedStoreId(e.target.value)}
                    className="accent-blue-600"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                      {store.store_name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {store.store_code || store.store_url || ''}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedStoreId || submitting || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            {t('channel.push_drafts') || '生成草稿'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreSelectorModal;
